<template>
  <div v-if="records_lists.length" class="box-border relative w-full h-full bg-white rounded-lg">
    <div class="w-full h-[54px] p-2 dark:text-white text-xl font-medium flex justify-between rounded-md">
      <div>{{ label }}</div>
      <RecordActionsDropDown
        v-if="records_lists && !editing_disabled"
        :object-type="records_lists[0].type"
        :page-reference="PAGE_REFERENCES.PROSPECTING_GROWTH_MATRIX"
        :selected-records="Array.from(selected_records)" />
    </div>

    <!-- TO LOAD COLORS -->
    <div class="absolute opacity-0">
      <div class="text-account-1 border-account-1 bg-account-1" />
      <div class="text-account-2 border-account-2 bg-account-2" />
      <div class="text-account-3 border-account-3 bg-account-3" />
      <div class="text-account-4 border-account-4 bg-account-4" />
      <div class="text-account-5 border-account-5 bg-account-5" />

      <div class="text-account-1 border-account-1 bg-account-1/25" />
      <div class="text-account-2 border-account-2 bg-account-2/25" />
      <div class="text-account-3 border-account-3 bg-account-3/25" />
      <div class="text-account-4 border-account-4 bg-account-4/25" />
      <div class="text-account-5 border-account-5 bg-account-5/25" />
    </div>

    <div class="flex h-[45%] relative">
      <div class="w-1/2 h-full">
        <div class="w-full p-2 text-xl text-grey-light-body dark:text-dark-body-text text-ellipsis">
          {{ records_lists[0]?.name }}
        </div>

        <div v-if="records_lists[0]?.description" class="px-2 pb-1 text-common-grey">
          {{ records_lists[0]?.description }}
        </div>
        <div v-else class="px-2 pb-1 opacity-0 text-common-grey">
          {{ $t("Null") }}
        </div>

        <div class="bg-white dark:bg-transparent border rounded-tl-lg border-[#B3CAD6] dark:border-dark-divider-border h-full relative">
          <div
            v-for="(circle, i) in circles_1"
            :key="circle.id"
            class="absolute duration-500 transition-width"
            :style="{
              left: `${circle.left}%`,
              width: `${circle.hover ? (circle.diameter < 2.4 ? '3.125' : circle.diameter * 1.25) : circle.diameter}%`,
              paddingTop: `${circle.diameter * 0.25}%`,
              top: `${circle.top}%`
            }"
            @click="handleClick(i, 1)"
            @mouseleave="handleMouseLeave(i, 1)"
            @mouseover="handleMouseEnter(i, 1)"
            @mouseenter="handleMouseEnter(i, 1)">
            <div
              :style="{
                width: `125%`,
                paddingTop: circle.selected ? `110%` : `120%`,
                top: '5%',
                left: '5%',
                borderWidth: circle.selected ? '2.5px' : '1px',
                color: circle.color,
                backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                borderColor: circle.color
              }"
              :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
              <span
                v-show="circle.diameter > 2.4 || circle.hover"
                :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
            </div>

            <Popper :show="circle.hover">
              <div
                :style="{
                  width: `125%`,
                  paddingTop: circle.selected ? `110%` : `120%`,
                  top: '5%',
                  left: '5%',
                  borderWidth: circle.selected ? '2.5px' : '1px',
                  color: circle.color,
                  backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                  borderColor: circle.color
                }"
                :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
                <span
                  v-show="circle.diameter > 2.4 || circle.hover"
                  :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                  :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
              </div>

              <template #content>
                <div
                  :class="[
                    `bg-white dark:bg-dark-box-background w-56 rounded-lg  ${circle.selected ? `border-2` : 'border'} border-${
                      circle.selected ? `blue-light` : 'border-[#B3CAD6] dark:border-dark-divider-border'
                    }`
                  ]">
                  <div :class="[` py-3 w-[90%] ml-[5%] border-b border-[#B3CAD6] dark:border-dark-divider-border`]">
                    <div class="flex items-center">
                      <div :style="{ backgroundColor: circle.color }" :class="[`w-3 h-3 rounded-full`]" />
                      <div
                        class="w-full ml-2 text-lg cursor-pointer text-grey-light-body dark:text-dark-body-text text-ellipsis"
                        @click="openRecordDetailsPage(records_lists[0], circle)">
                        {{ Record.from(circle).name }}
                      </div>
                      <input
                        v-model="circle.selected"
                        :checked="circle.selected"
                        type="checkbox"
                        :class="[`form-checkbox w-4 text-account-${circle.account} rounded h-4 dark:bg-dark-box-background ml-auto`]">
                    </div>
                    <div class="flex items-center">
                      <div class="text-common-grey">
                        {{ $utils.getMetricByReferenceName(swot_key_metric).name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        ${{ $utils.truncateNumber(circle.value) }}
                      </div>
                    </div>
                  </div>
                  <div class="p-2 mt-3 text-sm">
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Actual") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.base_value ? `$${$utils.truncateNumber(circle.base_value)}` : " - " }}
                      </div>
                    </div>
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Forecasted") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.forecasted_value ? `$${$utils.truncateNumber(circle.forecasted_value)}` : " - " }}
                      </div>
                    </div>
                    <div v-for="stat in circle.stats" :key="stat.id" class="flex mb-2">
                      <div class="text-common-grey">
                        {{ stat.name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ stat.value ? `$${$utils.truncateNumber(stat.value)}` : " - " }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Popper>
            <input
              v-if="circle.selected"
              v-model="circle.selected"
              checked
              :class="[`z-10 cursor-pointer absolute top-0 left-full ml-3 rounded-md text-account-${circle.account}`]"
              type="checkbox"
              @click="handleMouseLeave(i, 1)">
          </div>
        </div>
      </div>
      <div class="w-1/2 h-full">
        <div class="p-2 pb-1 text-xl text-right text-grey-light-body dark:text-dark-body-text">
          {{ records_lists[1]?.name }}
        </div>

        <div v-if="records_lists[1]?.description" class="px-2 pb-1 mb-1 text-right text-common-grey">
          {{ records_lists[1]?.description }}
        </div>
        <div v-else class="px-2 pb-1 mb-1 opacity-0 text-common-grey">
          {{ $t("Null") }}
        </div>

        <div
          class="bg-white dark:bg-transparent border-t rounded-tr-lg border-b border-r border-[#B3CAD6] dark:border-dark-divider-border h-full relative">
          <div
            v-for="(circle, i) in circles_2"
            :key="circle.id"
            class="absolute duration-500 transition-width"
            :style="{
              left: `${circle.left}%`,
              width: `${circle.hover ? (circle.diameter < 2.4 ? '3.125' : circle.diameter * 1.25) : circle.diameter}%`,
              paddingTop: `${circle.diameter * 0.25}%`,
              top: `${circle.top}%`
            }"
            @click="handleClick(i, 2)"
            @mouseleave="handleMouseLeave(i, 2)"
            @mouseover="handleMouseEnter(i, 2)"
            @mouseenter="handleMouseEnter(i, 2)">
            <div
              :style="{
                width: `125%`,
                paddingTop: circle.selected ? `110%` : `120%`,
                top: '5%',
                left: '5%',
                borderWidth: circle.selected ? '2.5px' : '1px',
                color: circle.color,
                backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                borderColor: circle.color
              }"
              :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
              <span
                v-show="circle.diameter > 2.4 || circle.hover"
                :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
            </div>
            <Popper :show="circle.hover">
              <div
                :style="{
                  width: `125%`,
                  paddingTop: circle.selected ? `110%` : `120%`,
                  top: '5%',
                  left: '5%',
                  borderWidth: circle.selected ? '2.5px' : '1px',
                  color: circle.color,
                  backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                  borderColor: circle.color
                }"
                :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
                <span
                  v-show="circle.diameter > 2.4 || circle.hover"
                  :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                  :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
              </div>

              <template #content>
                <div
                  :class="[
                    `bg-white dark:bg-dark-box-background w-56 rounded-lg  ${circle.selected ? `border-2` : 'border'} border-${
                      circle.selected ? `blue-light` : 'border-[#B3CAD6] dark:border-dark-divider-border'
                    }`
                  ]">
                  <div :class="[` py-3 w-[90%] ml-[5%] border-b border-[#B3CAD6] dark:border-dark-divider-border`]">
                    <div class="flex items-center">
                      <div :style="{ backgroundColor: circle.color }" :class="[`w-3 h-3 rounded-full`]" />
                      <div
                        class="w-full ml-2 text-lg cursor-pointer text-grey-light-body dark:text-dark-body-text text-ellipsis"
                        @click="openRecordDetailsPage(records_lists[1], circle)">
                        {{ Record.from(circle).name }}
                      </div>
                      <input
                        v-model="circle.selected"
                        :checked="circle.selected"
                        type="checkbox"
                        :class="[`form-checkbox w-4 text-account-${circle.account} rounded h-4 dark:bg-dark-box-background ml-auto`]">
                    </div>
                    <div class="flex items-center">
                      <div class="text-common-grey">
                        {{ $utils.getMetricByReferenceName(swot_key_metric).name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        ${{ $utils.truncateNumber(circle.value) }}
                      </div>
                    </div>
                  </div>
                  <div class="p-2 mt-3 text-sm">
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Actual") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.base_value ? `$${$utils.truncateNumber(circle.base_value)}` : " - " }}
                      </div>
                    </div>
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Forecasted") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.forecasted_value ? `$${$utils.truncateNumber(circle.forecasted_value)}` : " - " }}
                      </div>
                    </div>
                    <div v-for="stat in circle.stats" :key="stat.id" class="flex mb-2">
                      <div class="text-common-grey">
                        {{ stat.name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ stat.value ? `$${$utils.truncateNumber(stat.value)}` : " - " }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Popper>
            <input
              v-if="circle.selected"
              v-model="circle.selected"
              checked
              :class="[`z-10 cursor-pointer absolute top-0 left-full ml-3 rounded-md text-account-${circle.account}`]"
              type="checkbox"
              @click="handleMouseLeave(i, 2)">
          </div>
        </div>
      </div>

      <div class="w-full h-1 border-b absolute z-40 top-[100%]" />
    </div>

    <div class="flex h-[35%]">
      <div class="w-1/2 h-full">
        <div
          class="bg-white dark:bg-transparent border-r border-b border-l rounded-bl-lg h-full border-[#B3CAD6] dark:border-dark-divider-border relative">
          <div
            v-for="(circle, i) in circles_3"
            :key="circle.id"
            class="absolute duration-500 transition-width"
            :style="{
              left: `${circle.left}%`,
              width: `${circle.hover ? (circle.diameter < 2.4 ? '3.125' : circle.diameter * 1.25) : circle.diameter}%`,
              paddingTop: `${circle.diameter * 0.25}%`,
              top: `${circle.top}%`
            }"
            @click="handleClick(i, 3)"
            @mouseleave="handleMouseLeave(i, 3)"
            @mouseover="handleMouseEnter(i, 3)"
            @mouseenter="handleMouseEnter(i, 3)">
            <div
              :style="{
                width: `125%`,
                paddingTop: circle.selected ? `110%` : `120%`,
                top: '5%',
                left: '5%',
                borderWidth: circle.selected ? '2.5px' : '1px',
                color: circle.color,
                backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                borderColor: circle.color
              }"
              :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
              <span
                v-show="circle.diameter > 2.4 || circle.hover"
                :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
            </div>

            <Popper :show="circle.hover">
              <div
                :style="{
                  width: `125%`,
                  paddingTop: circle.selected ? `110%` : `120%`,
                  top: '5%',
                  left: '5%',
                  borderWidth: circle.selected ? '2.5px' : '1px',
                  color: circle.color,
                  backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                  borderColor: circle.color
                }"
                :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
                <span
                  v-show="circle.diameter > 2.4 || circle.hover"
                  :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                  :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
              </div>

              <template #content>
                <div
                  :class="[
                    `bg-white dark:bg-dark-box-background w-56 rounded-lg  ${circle.selected ? `border-2` : 'border'} border-${
                      circle.selected ? `blue-light` : 'border-[#B3CAD6] dark:border-dark-divider-border'
                    }`
                  ]">
                  <div :class="[` py-3 w-[90%] ml-[5%] border-b border-[#B3CAD6] dark:border-dark-divider-border`]">
                    <div class="flex items-center">
                      <div :style="{ backgroundColor: circle.color }" :class="[`w-3 h-3 rounded-full`]" />
                      <div
                        class="w-full ml-2 text-lg cursor-pointer text-grey-light-body dark:text-dark-body-text text-ellipsis"
                        @click="openRecordDetailsPage(records_lists[3], circle)">
                        {{ Record.from(circle).name }}
                      </div>
                      <input
                        v-model="circle.selected"
                        :checked="circle.selected"
                        type="checkbox"
                        :class="[`form-checkbox w-4 text-account-${circle.account} rounded h-4 dark:bg-dark-box-background ml-auto`]">
                    </div>
                    <div class="flex items-center">
                      <div class="text-common-grey">
                        {{ $utils.getMetricByReferenceName(swot_key_metric).name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        ${{ $utils.truncateNumber(circle.value) }}
                      </div>
                    </div>
                  </div>
                  <div class="p-2 mt-3 text-sm">
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Actual") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.base_value ? `$${$utils.truncateNumber(circle.base_value)}` : " - " }}
                      </div>
                    </div>
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Forecasted") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.forecasted_value ? `$${$utils.truncateNumber(circle.forecasted_value)}` : " - " }}
                      </div>
                    </div>
                    <div v-for="stat in circle.stats" :key="stat.id" class="flex mb-2">
                      <div class="text-common-grey">
                        {{ stat.name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ stat.value ? `$${$utils.truncateNumber(stat.value)}` : " - " }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Popper>
            <input
              v-if="circle.selected"
              v-model="circle.selected"
              checked
              :class="[`z-10 cursor-pointer absolute top-0 left-full ml-3 rounded-md text-account-${circle.account}`]"
              type="checkbox"
              @click="handleMouseLeave(i, 3)">
          </div>
        </div>
        <div class="p-2 pb-1 text-xl text-grey-light-body dark:text-dark-body-text">
          {{ records_lists[2]?.name }}
        </div>
        <div v-if="records_lists[2]?.description" class="px-2 pb-1 text-common-grey">
          {{ records_lists[2]?.description }}
        </div>
        <div v-else class="px-2 pb-1 opacity-0 text-common-grey">
          {{ $t("Null") }}
        </div>
      </div>
      <div class="w-1/2 h-full">
        <div
          class="bg-white dark:bg-transparent border-r border-b h-full rounded-br-lg border-[#B3CAD6] dark:border-dark-divider-border relative">
          <div
            v-for="(circle, i) in circles_4"
            :key="circle.id"
            class="absolute duration-500 transition-width"
            :style="{
              left: `${circle.left}%`,
              width: `${circle.hover ? (circle.diameter < 2.4 ? '3.125' : circle.diameter * 1.25) : circle.diameter}%`,
              paddingTop: `${circle.diameter * 0.25}%`,
              top: `${circle.top}%`
            }"
            @click="handleClick(i, 4)"
            @mouseleave="handleMouseLeave(i, 4)"
            @mouseover="handleMouseEnter(i, 4)"
            @mouseenter="handleMouseEnter(i, 4)">
            <div
              :style="{
                width: `125%`,
                paddingTop: circle.selected ? `110%` : `120%`,
                top: '5%',
                left: '5%',
                borderWidth: circle.selected ? '2.5px' : '1px',
                color: circle.color,
                backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                borderColor: circle.color
              }"
              :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
              <span
                v-show="circle.diameter > 2.4 || circle.hover"
                :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
            </div>
            <Popper :show="circle.hover">
              <div
                :style="{
                  width: `125%`,
                  paddingTop: circle.selected ? `110%` : `120%`,
                  top: '5%',
                  left: '5%',
                  borderWidth: circle.selected ? '2.5px' : '1px',
                  color: circle.color,
                  backgroundColor: `rgba(${hexToRgb(circle.color).r},${hexToRgb(circle.color).g},${hexToRgb(circle.color).b},0.25)`,
                  borderColor: circle.color
                }"
                :class="[` text-account-${circle.account} cursor-pointer duration-500 transition  border rounded-full absolute`]">
                <span
                  v-show="circle.diameter > 2.4 || circle.hover"
                  :style="{ transform: 'translate(-50%, -50%)', fontSize: '11px' }"
                  :class="[`glow absolute top-1/2 left-1/2 translate-y-2/4 text-sm font-${circle.selected ? 'bold' : 'normal'}`]">${{ $utils.truncateNumber(circle.value) }}</span>
              </div>

              <template #content>
                <div
                  :class="[
                    `bg-white dark:bg-dark-box-background w-56 rounded-lg  ${circle.selected ? `border-2` : 'border'} border-${
                      circle.selected ? `blue-light` : 'border-[#B3CAD6] dark:border-dark-divider-border'
                    }`
                  ]">
                  <div :class="[` py-3 w-[90%] ml-[5%] border-b border-[#B3CAD6] dark:border-dark-divider-border`]">
                    <div class="flex items-center">
                      <div :style="{ backgroundColor: circle.color }" :class="[`w-3 h-3 rounded-full`]" />
                      <div
                        class="w-full ml-2 text-lg cursor-pointer text-grey-light-body dark:text-dark-body-text text-ellipsis"
                        @click="openRecordDetailsPage(records_lists[4], circle)">
                        {{ Record.from(circle).name }}
                      </div>
                      <input
                        v-model="circle.selected"
                        :checked="circle.selected"
                        type="checkbox"
                        :class="[`form-checkbox w-4 text-account-${circle.account} rounded h-4 dark:bg-dark-box-background ml-auto`]">
                    </div>
                    <div class="flex items-center">
                      <div class="text-common-grey">
                        {{ $utils.getMetricByReferenceName(swot_key_metric).name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        ${{ $utils.truncateNumber(circle.value) }}
                      </div>
                    </div>
                  </div>
                  <div class="p-2 mt-3 text-sm">
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Actual") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.base_value ? `$${$utils.truncateNumber(circle.base_value)}` : " - " }}
                      </div>
                    </div>
                    <div class="flex mb-2">
                      <div class="text-common-grey">
                        {{ $t("Forecasted") }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ circle.forecasted_value ? `$${$utils.truncateNumber(circle.forecasted_value)}` : " - " }}
                      </div>
                    </div>
                    <div v-for="stat in circle.stats" :key="stat.id" class="flex mb-2">
                      <div class="text-common-grey">
                        {{ stat.name }}
                      </div>
                      <div class="ml-auto text-grey-light-body dark:text-dark-body-text">
                        {{ stat.value ? `$${$utils.truncateNumber(stat.value)}` : " - " }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Popper>
            <input
              v-if="circle.selected"
              v-model="circle.selected"
              checked
              :class="[`z-10 cursor-pointer absolute top-0 left-full ml-3 rounded-md text-account-${circle.account}`]"
              type="checkbox"
              @click="handleMouseLeave(i, 4)">
          </div>
        </div>
        <div class="p-2 pb-1 text-xl text-right text-grey-light-body dark:text-dark-body-text">
          {{ records_lists[3]?.name }}
        </div>

        <div v-if="records_lists[3]?.description" class="px-2 pb-1 text-right text-common-grey">
          {{ records_lists[3]?.description }}
        </div>
        <div v-else class="px-2 pb-1 opacity-0 text-common-grey">
          {{ $t("Null") }}
        </div>
      </div>
    </div>
  </div>

  <div
    v-else-if="records_lists.length == 0 && is_loading && $store.state.settings.swot_objects_key_metrics.length"
    class="bg-white dark:bg-dark-box-background flex justify-center min-h-[500px] overflow-hidden rounded-lg p-5">
    <div class="flex flex-col justify-center">
      <h3 class="relative mt-2 text-2xl font-normal text-gray-500 capitalize">
        {{ $t("Loading growth matrix") }} <LoadingSpinner class="" />
      </h3>
    </div>
  </div>
  <div v-else class="bg-white dark:bg-dark-box-background flex justify-center min-h-[500px] overflow-hidden rounded-lg p-5">
    <div class="flex flex-col items-center justify-center">
      <Info class="w-20 h-20 text-blue" aria-hidden="true" />
      <h3 class="mt-2 text-xl font-normal text-black dark:text-white">
        {{ $t("No Growth Matrix found") }}
      </h3>
      <p class="mt-2 font-normal text-gray-500 text-md">
        {{ $t("You don't have any Growth Matrix right now. You can easily add Suggested lists via admin tools.") }}
      </p>
      <div class="m-2 primary-btn w-fit" @click="$router.push('/organization/?tab=suggested')">
        <Settings class="ml-1 w-7 h-7" />
        <div class="pl-1">
          <span>{{ $t("Setup Growth Matrix") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dataService from "@/services/data.service"
import RecordActionsDropDown from "@/components/shared/RecordActionsDropDown.vue"
import { Info, Settings } from "lucide-vue-next"
import Popper from "vue3-popper"
import { PAGE_REFERENCES } from "@/store/constants/references"
import { utils } from "@/store/helpers"
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue"
import { Record } from "@/models/record"

import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Settings,
    Info,
    RecordActionsDropDown,
    Popper,
    LoadingSpinner
  },

  inject: ["canUserEditLayout"],
  props: {
    dataSource: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: null
    }
  },
  emits: ["selectedRecords"],
  data() {
    return {
      Record,
      is_loading: false,
      PAGE_REFERENCES,
      object_type_view: null,
      selected_records: new Set(),
      records_lists: [],
      key_metric: null,
      circles_1: [],
      circles_2: [],
      circles_3: [],

      circles_4: [],
      swot_key_metric: null
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  created() {
    this.register_listeners()
    this.getGrowthCompanies()
  },
  activated() {
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.REMOVE_GROWTH_LIST_FROM_GROWTH_MATRIX_COMPONENT, (list_id) => {
        utils.removeItem(this.records_lists, list_id)
      })
      this.$emitter.on(ALL_EMITTERS.GROWTH_MATRIX_CREATED_IN_GROWTH_MATRIX_COMPONENT, () => {
        this.getGrowthCompanies(false)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.REMOVE_GROWTH_LIST_FROM_GROWTH_MATRIX_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.GROWTH_MATRIX_CREATED_IN_GROWTH_MATRIX_COMPONENT)
    },
    openRecordDetailsPage(list, record) {
      if (record.type == "company") {
        this.$router.push(`/company/${record.id}`)
      } else {
        this.$router.push(`/company/${record.parent}/${list.type}/${record.id}`)
      }
    },

    hexToRgb(hex) {
      var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          }
        : {
            r: 220,
            g: 220,
            b: 220
          }
    },
    handleMouseEnter(i, n) {
      this[`circles_${n}`].forEach((c, j) => {
        if (j !== i) {
          const circle_obj = c
          circle_obj.hover = false
          this[`circles_${n}`].splice(j, 1, circle_obj)
        }
      })

      const circles_copy = [...this[`circles_${n}`]]
      const circle_obj = circles_copy[i]
      circle_obj.hover = true
      this[`circles_${n}`].splice(i, 1, circle_obj)
    },
    handleMouseLeave(i, n) {
      const circles_copy = [...this[`circles_${n}`]]
      const circle_obj = circles_copy[i]
      circle_obj.hover = false
      this[`circles_${n}`].splice(i, 1, circle_obj)
    },

    handleClick(i, n) {
      const circles_copy = [...this[`circles_${n}`]]
      const circle_obj = circles_copy[i]

      this.toggleSelectedRecord(circle_obj, !circle_obj.selected)
      circle_obj.selected = !circle_obj.selected
      this[`circles_${n}`].splice(i, 1, circle_obj)
    },
    positionCircles(circles) {
      const circlesArrayCopy = [...circles]

      //calcultion to calculate median, max and min diameters based on number of values
      const circlesArrayOddLength = circlesArrayCopy.length % 2 ? circlesArrayCopy.length : circlesArrayCopy.length - 1
      const valueToAdd = (circlesArrayOddLength - 3) / 4
      const medianDiameter = 100 / (circlesArrayCopy.length + valueToAdd)
      const maxDiameter = medianDiameter * 1.5
      const minDiameter = medianDiameter * 0.5

      const medianToMaxDiameterRange = maxDiameter - medianDiameter
      const medianToMinDiameterRange = medianDiameter - minDiameter

      if (circlesArrayCopy.length === 1) {
        //set diameter of only one value by max
        const circleValue = { ...circlesArrayCopy[0] }
        circleValue.diameter = maxDiameter
        circlesArrayCopy.splice(0, 1, circleValue)
      } else if (circlesArrayCopy.length === 2) {
        //set diameter of only first value by median
        const firstValue = { ...circlesArrayCopy[0] }
        firstValue.diameter = medianDiameter
        circlesArrayCopy.splice(0, 1, firstValue)

        //set diameter of second value by maximum
        const secondValue = { ...circlesArrayCopy[1] }
        secondValue.diameter = maxDiameter
        circlesArrayCopy.splice(1, 1, secondValue)
      } else if (circlesArrayCopy.length > 2) {
        const medianPostion = circlesArrayCopy.length % 2 == 0 ? circlesArrayCopy.length / 2 : (circlesArrayCopy.length - 1) / 2

        const medianToMaxValuesRange = circlesArrayCopy[circlesArrayCopy.length - 1].value - circlesArrayCopy[medianPostion].value
        const medianToMinValuesRange = circlesArrayCopy[medianPostion].value - circlesArrayCopy[0].value
        //set diameter of median value
        const medianCircleValue = { ...circlesArrayCopy[medianPostion] }
        medianCircleValue.diameter = medianDiameter
        circlesArrayCopy.splice(medianPostion, 1, medianCircleValue)

        //set diameter of maximum circle value
        const maximumCircleValue = { ...circlesArrayCopy[circlesArrayCopy.length - 1] }
        maximumCircleValue.diameter = maxDiameter
        circlesArrayCopy.splice(circlesArrayCopy.length - 1, 1, maximumCircleValue)

        //set diameter of minimum value
        const minimumCircleValue = { ...circlesArrayCopy[0] }
        minimumCircleValue.diameter = minDiameter
        circlesArrayCopy.splice(0, 1, minimumCircleValue)

        let countdown = circlesArrayCopy.length - 1
        while (countdown > 1) {
          countdown--
          const circleObj = { ...circlesArrayCopy[countdown] }

          if (countdown !== medianPostion) {
            if (countdown > medianPostion) {
              const deviationFromMedian = circleObj.value - circlesArrayCopy[medianPostion].value
              const diameterCalc = (deviationFromMedian / medianToMaxValuesRange) * medianToMaxDiameterRange + medianDiameter

              circleObj.diameter = diameterCalc
              circlesArrayCopy.splice(countdown, 1, circleObj)
            } else {
              const deviationFromMedian = circlesArrayCopy[medianPostion].value - circleObj.value
              const diameterCalc = medianDiameter - (deviationFromMedian / medianToMinValuesRange) * medianToMinDiameterRange

              circleObj.diameter = diameterCalc
              circlesArrayCopy.splice(countdown, 1, circleObj)
            }
          }
        }
      }

      let newCirclesArray = []

      for (let j = circlesArrayCopy.length - 1; j >= 0; j--) {
        const circle = circlesArrayCopy[j]
        const maxTop = 90 - circle.diameter * 2
        const maxLeft = 90 - circle.diameter

        let randomLeftValue = null
        let randomTopValue = null
        if (j == circlesArrayCopy.length - 1) {
          randomLeftValue = Math.random() * (0 - maxLeft) + maxLeft
          randomTopValue = Math.random() * (0 - maxTop) + maxTop
          newCirclesArray.push({ ...circle, left: randomLeftValue, top: randomTopValue })
        } else {
          let foundSpot = false
          let counter = 0
          while (!foundSpot && counter < 1000) {
            counter++
            randomLeftValue = Math.random() * (0 - maxLeft) + maxLeft
            randomTopValue = Math.random() * (0 - maxTop) + maxTop
            for (let i = 0; i < newCirclesArray.length; i++) {
              const newCircleValue = { ...newCirclesArray[i] }

              if (
                randomTopValue + circle.diameter * 2 > newCircleValue.top &&
                randomTopValue - circle.diameter * 2 < newCircleValue.top + newCircleValue.diameter
              ) {
                if (
                  randomLeftValue + circle.diameter > newCircleValue.left &&
                  randomLeftValue - circle.diameter < newCircleValue.left + newCircleValue.diameter
                ) {
                  break
                }
              }

              if (i == newCirclesArray.length - 1) {
                foundSpot = true
                newCirclesArray.push({ ...circle, left: randomLeftValue, top: randomTopValue })
                break
              }
            }
          }
        }
      }

      if (newCirclesArray[0].diameter >= 100) {
        newCirclesArray[0].diameter = 30
        newCirclesArray[0].top = 10
        newCirclesArray[0].left = 30
      }

      return newCirclesArray
    },
    getGrowthCompanies(showLoading = true) {
      this.is_loading = true
      if (showLoading) {
        this.$store.dispatch("ui/showLoading")
      }
      dataService
        .getMatrixCompanies({ type: "growth_matrix", include_rollup_metrics: true, include_records: true, page_size: 25 })
        .then((result) => {
          this.records_lists = []
          this.$store.dispatch("ui/hideLoading")

          result.data.data.map((list) => {
            list["allChecked"] = false
            list.records.results.map((x) => {
              x["checked"] = false
              return x
            })
            this.records_lists.push(list)

            // this.loadMoreCompanies(list)
            return list
          })
          this.drawCircles()
          this.is_loading = false
        })
        .catch(() => {
          this.is_loading = false
          this.$store.dispatch("ui/hideLoading")
        })
    },
    drawCircles() {
      if (this.records_lists.length) {
        this.swot_key_metric = this.$utils.getMetricById(
          this.$store.state.settings.swot_objects_key_metrics[this.records_lists[0].object_type]
        ).reference_name
        //Calculate or fetch circles_1
        if (this.records_lists[0]) {
          const circles_1_calc = []
          this.records_lists[0].records.results.forEach((r) => {
            if (r.values[this.swot_key_metric]) {
              circles_1_calc.push({
                ...r,
                value: r.values[this.swot_key_metric],
                color: this.$utils.calculateRecordMetricColor(
                  this.$utils.getMetricByReferenceName(this.swot_key_metric).id,
                  r.values[this.swot_key_metric]
                ).color
              })
            }
          })

          const circles_1_calc_sorted = circles_1_calc.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
          const circles_1_local_storage = window.localStorage.getItem("circles_1")

          if (!circles_1_local_storage) {
            this.circles_1 = this.positionCircles(circles_1_calc_sorted)

            window.localStorage.setItem("circles_1", JSON.stringify(this.circles_1))
          } else {
            const circle_1_local_storage_Array = JSON.parse(circles_1_local_storage)
            if (circle_1_local_storage_Array.length !== circles_1_calc_sorted.length) {
              this.circles_1 = this.positionCircles(circles_1_calc_sorted)
              window.localStorage.setItem("circles_1", JSON.stringify(this.circles_1))
            } else {
              let changes_done = false
              circle_1_local_storage_Array.map((circle_s) => {
                const index = circles_1_calc_sorted.findIndex((circle) => circle.id == circle_s.id)
                if (index == "-1") {
                  changes_done = true
                } else {
                  const circle_obj = circles_1_calc_sorted[index]
                  if (circle_s.value !== circle_obj.value) {
                    changes_done = true
                  }
                }
              })
              if (changes_done) {
                this.circles_1 = this.positionCircles(circles_1_calc_sorted)
                window.localStorage.setItem("circles_1", JSON.stringify(this.circles_1))
              } else {
                this.circles_1 = JSON.parse(circles_1_local_storage)
              }
            }
          }
        }

        if (this.records_lists[1]) {
          // Calculate or fetch circles_2
          const circles_2_calc = []
          this.records_lists[1].records.results.forEach((r) => {
            if (r.values[this.swot_key_metric]) {
              circles_2_calc.push({
                ...r,
                value: r.values[this.swot_key_metric],
                color: this.$utils.calculateRecordMetricColor(
                  this.$utils.getMetricByReferenceName(this.swot_key_metric).id,
                  r.values[this.swot_key_metric]
                ).color
              })
            }
          })
          const circles_2_calc_sorted = circles_2_calc.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
          const circles_2_local_storage = window.localStorage.getItem("circles_2")
          if (!circles_2_local_storage) {
            this.circles_2 = this.positionCircles(circles_2_calc_sorted)
            window.localStorage.setItem("circles_2", JSON.stringify(this.circles_2))
          } else {
            const circle_1_local_storage_Array = JSON.parse(circles_2_local_storage)
            if (circle_1_local_storage_Array.length !== circles_2_calc_sorted.length) {
              this.circles_2 = this.positionCircles(circles_2_calc_sorted)
              window.localStorage.setItem("circles_2", JSON.stringify(this.circles_2))
            } else {
              let changes_done = false
              circle_1_local_storage_Array.map((circle_s) => {
                const index = circles_2_calc_sorted.findIndex((circle) => circle.id == circle_s.id)
                if (index == "-1") {
                  changes_done = true
                } else {
                  const circle_obj = circles_2_calc_sorted[index]
                  if (circle_s.value !== circle_obj.value) {
                    changes_done = true
                  }
                }
              })
              if (changes_done) {
                this.circles_2 = this.positionCircles(circles_2_calc_sorted)
                window.localStorage.setItem("circles_2", JSON.stringify(this.circles_2))
              } else {
                this.circles_2 = JSON.parse(circles_2_local_storage)
              }
            }
          }
        }

        if (this.records_lists[2]) {
          // Calculate or fetch circles_3
          const circles_3_calc = []
          this.records_lists[2].records.results.forEach((r) => {
            if (r.values[this.swot_key_metric]) {
              circles_3_calc.push({
                ...r,
                value: r.values[this.swot_key_metric],
                color: this.$utils.calculateRecordMetricColor(
                  this.$utils.getMetricByReferenceName(this.swot_key_metric).id,
                  r.values[this.swot_key_metric]
                ).color
              })
            }
          })
          const circles_3_calc_sorted = circles_3_calc.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
          const circles_3_local_storage = window.localStorage.getItem("circles_3")
          if (!circles_3_local_storage) {
            this.circles_3 = this.positionCircles(circles_3_calc_sorted)
            window.localStorage.setItem("circles_3", JSON.stringify(this.circles_3))
          } else {
            const circle_1_local_storage_Array = JSON.parse(circles_3_local_storage)
            if (circle_1_local_storage_Array.length !== circles_3_calc_sorted.length) {
              this.circles_3 = this.positionCircles(circles_3_calc_sorted)
              window.localStorage.setItem("circles_3", JSON.stringify(this.circles_3))
            } else {
              let changes_done = false
              circle_1_local_storage_Array.map((circle_s) => {
                const index = circles_3_calc_sorted.findIndex((circle) => circle.id == circle_s.id)
                if (index == "-1") {
                  changes_done = true
                } else {
                  const circle_obj = circles_3_calc_sorted[index]
                  if (circle_s.value !== circle_obj.value) {
                    changes_done = true
                  }
                }
              })
              if (changes_done) {
                this.circles_3 = this.positionCircles(circles_3_calc_sorted)
                window.localStorage.setItem("circles_3", JSON.stringify(this.circles_3))
              } else {
                this.circles_3 = JSON.parse(circles_3_local_storage)
              }
            }
          }
        }
        ``

        if (this.records_lists[3]) {
          // Calculate or fetch circles_4
          const circles_4_calc = []
          this.records_lists[3].records.results.forEach((r) => {
            if (r.values[this.swot_key_metric]) {
              circles_4_calc.push({
                ...r,
                value: r.values[this.swot_key_metric],
                color: this.$utils.calculateRecordMetricColor(
                  this.$utils.getMetricByReferenceName(this.swot_key_metric).id,
                  r.values[this.swot_key_metric]
                ).color
              })
            }
          })
          const circles_4_calc_sorted = circles_4_calc.sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
          const circles_4_local_storage = window.localStorage.getItem("circles_4")
          if (!circles_4_local_storage) {
            this.circles_4 = this.positionCircles(circles_4_calc_sorted)
            window.localStorage.setItem("circles_4", JSON.stringify(this.circles_4))
          } else {
            const circle_1_local_storage_Array = JSON.parse(circles_4_local_storage)
            if (circle_1_local_storage_Array.length !== circles_4_calc_sorted.length) {
              this.circles_4 = this.positionCircles(circles_4_calc_sorted)
              window.localStorage.setItem("circles_4", JSON.stringify(this.circles_4))
            } else {
              let changes_done = false
              circle_1_local_storage_Array.map((circle_s) => {
                const index = circles_4_calc_sorted.findIndex((circle) => circle.id == circle_s.id)
                if (index == "-1") {
                  changes_done = true
                } else {
                  const circle_obj = circles_4_calc_sorted[index]
                  if (circle_s.value !== circle_obj.value) {
                    changes_done = true
                  }
                }
              })
              if (changes_done) {
                this.circles_4 = this.positionCircles(circles_4_calc_sorted)
                window.localStorage.setItem("circles_4", JSON.stringify(this.circles_4))
              } else {
                this.circles_4 = JSON.parse(circles_4_local_storage)
              }
            }
          }
        }
      }
    },
    toggleSelectedRecord(record, isChecked) {
      if (isChecked) {
        this.selected_records.add(record.id)
      } else {
        this.selected_records.delete(record.id)
      }
      this.$emit("selectedRecords", Array.from(this.selected_records))
    },
    async loadMoreCompanies(list) {
      if (list?.records.next) {
        let filter = { filter: list.filter, object_types: [list.type], filter_metrics: list.filter_metrics }
        return dataService.getRecordsByUrl(list.records.next, filter).then((result) => {
          list.records.next = result.data.next
          list.records.results.push(...result.data.results)
          return list
        })
      }
    }
  }
}
</script>
